'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'

import ButtonCartGroup from '@/components/common/ButtonCartGroup'
import CustomImage from '@/components/common/CustomImage'
import ProductInventory from '@/components/common/ProductInventory'
import ProductTags from '@/components/common/ProductTags'
import Currency from '@/components/ui/Currency'
import ShowUnit from '@/components/ui/ShowUnit'
import { useConfigContext } from '@/context/ConfigProvider'
import { cn, loadImage } from '@/lib/utils'
import { ProductFullSearchData } from '@/types/product.type'

import ZoomImage from './ZoomImage'
import { useEffect, useState } from 'react'
import Tooltips from '@/components/ui/Tooltips'

type ProductCardProps = {
  product: ProductFullSearchData
  showButton?: boolean
  showZoomImage?: boolean
}

export default function ProductCard({ product, showButton = true, showZoomImage = true }: ProductCardProps) {
  const { locale } = useConfigContext()
  const t = useTranslations('common')
  const [isDisabled, setIsDisabled] = useState(false)


  const checkProductTags = (tags: ProductFullSearchData['product_tags'], tagIds: number[]) => {
    let hasBannedTag = false
    for (const tagId of tagIds) {
      if (tags?.some(tag => tag.product_tag_id === tagId)) {
        hasBannedTag = true
        break
      }
    }
    return hasBannedTag
  }

  useEffect(() => {
    if (!product) return
    if (
      // product.product_tag_id === 18 || product.product_tag_id === 15 || 
      checkProductTags(product.product_tags, [18, 15])) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [product])

  return (
    <div className="rounded-[5px] border border-bordercolor hover:border-primary">
      {showZoomImage ? (
        <ZoomImage href={`/${product.slug}`} src={loadImage('large', product.image_id)} alt={product.name}>
          {product?.product_tags?.length > 0 && <ProductTags tags={product.product_tags} />}
          {product.price_change && (
            <span className="tag-discount !text-[11px]">
              {t('discount')} {Math.abs(product.price_change)}%
            </span>
          )}
        </ZoomImage>
      ) : (
        <div className="image relative">
          <Link href={`/${product.slug}`} target="_blank">
            <CustomImage
              src={loadImage('large', product.image_id)}
              fallbackSrc="/assets/images/no-image.png"
              alt={product.name}
              width={0}
              height={0}
              sizes="100vw"
              className={cn('max-h-[200px] aspect-square w-full h-auto rounded-tl-[5px] rounded-tr-[5px]')}
            />
          </Link>
        </div>
      )}
      <div className="p-2">
        <div className="min-h-[67px] relative  flex items-center group cursor-pointer mt-2">
          <Link href={`/${product.slug}`} target="_blank" className="overflow-hidden text-ellipsis break-words line-clamp-3 text-sm font-normal leading-[21px] hover:text-primary">
            {product.name}
          </Link>
          <Tooltips className='z-[99] font-normal' placement="bottom" style="warning" message={`${product?.name}`} />
        </div>
        {/* <div className="relative flex items-center group cursor-pointer w-fit">
          <Link href={`/${product?.slug}`} target="_blank" className="text-sm hover:underline  text-primary">
            {`IC${product.id}`}
          </Link>
          <Tooltips className='z-[99] font-normal' placement="right" style="warning" message={`IC${product?.id} Thegioiic part#`} />
        </div> */}
        <div className="text-gray-800 text-sm mt-1">
          <i>{product.supplier.name}</i>
        </div>
        <div className="mt-2 pb-1 text-sm">
          <Currency className="font-bold" currency={product.price} lang={locale} />/<ShowUnit unit={product.unit} />
        </div>
        <ProductInventory product={product} />
        {showButton && (
          <ButtonCartGroup
            prices={product.prices}
            className="mt-2 pt-1"
            buttonSize="xs"
            multiples={product.multiples}
            productId={product.id}
            product={product}
            minQuantity={product.minimum_order_quantity}
            isDisabled={isDisabled}
          />
        )}
      </div>
    </div>
  )
}
